import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../api';

const ChatComponent = ({ predictionId, propertyId, currentRent }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [error, setError] = useState(null);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [inputMode, setInputMode] = useState(null); // 'exact_rent' | 'percentage' | null
  const [structuredInput, setStructuredInput] = useState('');
  // Prior conversations for this listing (loaded behind a toggle to avoid clutter/perf)
  const [previousMessages, setPreviousMessages] = useState([]);
  const [showPrevious, setShowPrevious] = useState(false);
  const messagesEndRef = useRef(null);

  // Dragging state
  const [position, setPosition] = useState(null); // null means auto-position next to button
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const buttonRef = useRef(null);
  const chatRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start chat session when component opens
  const startChatSession = async () => {
    try {
      setIsLoading(true);
      setError(null);
    console.log("predictionId type:", typeof predictionId, "value:", predictionId);
    console.log("propertyId type:", typeof propertyId, "value:", propertyId);
      const response = await authFetch('/chat/start-session', {
        method: 'POST',
        body: JSON.stringify({
          prediction_id: predictionId,
          listing_id: propertyId
        })
      });

      setSessionId(response.session_id);

      // Load prior conversations for this listing (excluding the just-started session).
      try {
        const prev = await authFetch(`/chat/listing/${propertyId}/previous?exclude_session=${response.session_id}`);
        setPreviousMessages(prev?.messages || []);
      } catch (e) { /* non-fatal — just skip the previous-chats panel */ }

      setMessages([{
        id: 0,
        type: 'ai_response',
        content: "Hello, I am your AI prediction agent! First, yes, I am a robot! 🤖\n\nSecond, as you can see, I have given my prediction based on data and past feedback. However, if you think I'm off or want to add more info to help me learn to be better - \"Help me help you!\" (Did you get that Jerry Maguire reference??)\n\nTo make this more efficient and accurate, please click one of the following options:",
        timestamp: new Date().toISOString(),
        showQuickActions: true
      }]);
      
    } catch (err) {
      setError('Failed to start chat session');
      console.error('Chat session error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle quick action button clicks
  const handleQuickAction = async (action) => {
    // For exact rent and percentage, show structured input form instead of AI chat
    if (action === 'exact_rent' || action === 'percentage') {
      setInputMode(action);
      setStructuredInput('');
      setShowQuickActions(false);
      return;
    }

    let userMessage = '';
    let aiResponse = '';

    switch(action) {
      case 'unit_info':
        userMessage = 'I have information about this specific unit';
        aiResponse = 'Excellent! Please share any details about this specific unit that I should know. For example: features, condition, recent upgrades, views, outdoor space, or anything that makes this unit unique.';
        break;
      case 'building_info':
        userMessage = 'I have information about the building';
        aiResponse = 'Wonderful! Please share information about the building that applies to all units. For example: building quality, amenities, management, location advantages, or general building characteristics.';
        break;
      case 'market_insight':
        userMessage = 'I have general market insights to share';
        aiResponse = 'Awesome! Please share your market insights that could help me make better predictions. For example: "Water views typically add 10% to rent" or "Buildings near subway stations rent 15% higher."';
        break;
      default:
        return;
    }

    // Add user message
    const newUserMessage = {
      id: messages.length + 1,
      type: 'user_question',
      content: userMessage,
      timestamp: new Date().toISOString()
    };

    // Add AI response
    const newAiMessage = {
      id: messages.length + 2,
      type: 'ai_response',
      content: aiResponse,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, newUserMessage, newAiMessage]);
    setShowQuickActions(false);
  };

  // Submit structured feedback (exact rent or percentage) - skips AI
  const submitStructuredFeedback = async () => {
    if (!structuredInput.trim() || isLoading || !sessionId) return;

    const value = parseFloat(structuredInput.replace(/[,$%]/g, ''));
    if (isNaN(value)) {
      setError('Please enter a valid number');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        feedback_type: inputMode,
        value: value,
        scope: 'property'  // Both exact rent and percentage adjustments are unit-specific
      };

      const response = await authFetch(`/chat/${sessionId}/structured-feedback`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      // Add user message showing what they submitted
      const userMessage = {
        id: messages.length + 1,
        type: 'user_question',
        content: inputMode === 'exact_rent'
          ? `I believe the correct rent for this unit is $${value.toLocaleString()}`
          : `I think the prediction should be adjusted by ${value > 0 ? '+' : ''}${value}%`,
        timestamp: new Date().toISOString()
      };

      // Add confirmation message
      const confirmMessage = {
        id: messages.length + 2,
        type: 'ai_response',
        content: response.message || 'Thank you! Your feedback has been recorded and will be used to improve future predictions for this property.',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, userMessage, confirmMessage]);
      setInputMode(null);
      setStructuredInput('');

    } catch (err) {
      setError('Failed to submit feedback');
      console.error('Structured feedback error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel structured input mode
  const cancelStructuredInput = () => {
    setInputMode(null);
    setStructuredInput('');
    setShowQuickActions(true);
  };

  // Send message to AI
  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !sessionId) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user_question',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch(`/chat/${sessionId}/message`, {
        method: 'POST',
        body: JSON.stringify({
          message: inputMessage.trim()
        })
      });

      const aiMessage = {
        id: messages.length + 2,
        type: 'ai_response',
        content: response.ai_response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMessage]);

    } catch (err) {
      setError('Failed to send message');
      console.error('Send message error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Calculate position next to button - moved to openChat function for synchronous execution

  // Handle dragging
  const handleMouseDown = (e) => {
    if (e.target.closest('.chat-header-drag-area')) {
      setIsDragging(true);
      const chatRect = chatRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - chatRect.left,
        y: e.clientY - chatRect.top,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && chatRef.current) {
      const newLeft = e.clientX - dragOffset.x;
      const newTop = e.clientY - dragOffset.y;

      // Keep within viewport bounds
      const maxLeft = window.innerWidth - chatRef.current.offsetWidth;
      const maxTop = window.innerHeight - chatRef.current.offsetHeight;

      setPosition({
        left: Math.max(0, Math.min(newLeft, maxLeft)),
        top: Math.max(0, Math.min(newTop, maxTop)),
        right: 'auto',
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragOffset]);

  // Open chat and start session
  const openChat = () => {
    // Calculate position next to button before opening
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      // Position chat below and aligned with the button
      setPosition({
        top: buttonRect.bottom + 10,
        right: window.innerWidth - buttonRect.right,
      });
    }

    setIsChatOpen(true);
    if (!sessionId) {
      startChatSession();
    }
  };

  // Close chat
  const closeChat = () => {
    setIsChatOpen(false);
    setPosition(null); // Reset position when closing
  };

  // Styles
  const chatButtonStyle = {
    backgroundColor: '#000',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    transition: 'all 0.3s ease',
  };

  const chatContainerStyle = {
    position: 'fixed',
    ...(position || { bottom: '20px', right: '20px' }),
    width: '400px',
    height: '500px',
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '12px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1001,
    fontFamily: 'system-ui, -apple-system, sans-serif',
  };

  const chatHeaderStyle = {
    backgroundColor: '#000',
    color: 'white',
    padding: '15px 20px',
    borderRadius: '12px 12px 0 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'move',
    userSelect: 'none',
  };

  const messagesContainerStyle = {
    flex: 1,
    padding: '15px',
    overflowY: 'auto',
    backgroundColor: '#f8f9fa',
  };

  const messageStyle = (isUser) => ({
    marginBottom: '12px',
    padding: '12px 16px',
    borderRadius: '18px',
    maxWidth: '85%',
    wordWrap: 'break-word',
    backgroundColor: isUser ? '#000' : 'white',
    color: isUser ? 'white' : '#333',
    marginLeft: isUser ? 'auto' : '0',
    marginRight: isUser ? '0' : 'auto',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    fontSize: '14px',
    lineHeight: '1.4',
  });

  const inputContainerStyle = {
    padding: '15px',
    borderTop: '1px solid #eee',
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-end',
  };

  const inputStyle = {
    flex: 1,
    padding: '12px 16px',
    border: '1px solid #ddd',
    borderRadius: '20px',
    fontSize: '14px',
    resize: 'vertical',
    outline: 'none',
    minHeight: '44px',
    maxHeight: '120px',
    lineHeight: '1.4',
  };

  const sendButtonStyle = {
    backgroundColor: '#000',
    color: 'white',
    border: 'none',
    borderRadius: '20px',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: isLoading ? 'not-allowed' : 'pointer',
    opacity: isLoading ? 0.6 : 1,
    transition: 'all 0.3s ease',
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '0',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s',
  };

  const errorStyle = {
    color: '#dc3545',
    fontSize: '12px',
    padding: '8px 16px',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    margin: '0 15px 10px 15px',
  };

  const loadingDotsStyle = {
    display: 'flex',
    gap: '4px',
    padding: '12px 16px',
  };

  const dotStyle = {
    width: '6px',
    height: '6px',
    backgroundColor: '#666',
    borderRadius: '50%',
    animation: 'bounce 1.4s infinite ease-in-out',
  };

  const quickActionsContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '12px',
  };

  const quickActionButtonStyle = {
    backgroundColor: 'white',
    color: '#000',
    border: '2px solid #000',
    borderRadius: '12px',
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'left',
  };

  const hintBoxStyle = {
    marginTop: '10px',
    padding: '10px 12px',
    backgroundColor: '#f7f9fc',
    border: '1px solid #e3e8ef',
    borderRadius: '10px',
    fontSize: '12px',
    lineHeight: '1.7',
    color: '#555',
  };

  const backButtonStyle = {
    backgroundColor: '#f0f0f0',
    color: '#000',
    border: '1px solid #ddd',
    borderRadius: '12px',
    padding: '10px 16px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginTop: '12px',
    width: '100%',
    textAlign: 'center',
  };

  // Structured input styles
  const structuredInputContainerStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    margin: '12px 0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    border: '2px solid #000',
  };

  const structuredInputLabelStyle = {
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '12px',
    color: '#333',
  };

  const structuredInputRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  };

  const inputPrefixStyle = {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
  };

  const inputSuffixStyle = {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333',
  };

  const structuredInputFieldStyle = {
    flex: 1,
    padding: '12px 16px',
    fontSize: '18px',
    fontWeight: '600',
    border: '2px solid #ddd',
    borderRadius: '8px',
    outline: 'none',
    textAlign: 'center',
  };

  const structuredInputHintStyle = {
    fontSize: '12px',
    color: '#666',
    marginBottom: '16px',
  };

  const structuredButtonRowStyle = {
    display: 'flex',
    gap: '10px',
  };

  const cancelButtonStyle = {
    flex: 1,
    padding: '12px',
    fontSize: '14px',
    fontWeight: '600',
    border: '1px solid #ddd',
    borderRadius: '8px',
    backgroundColor: '#f5f5f5',
    color: '#666',
    cursor: 'pointer',
  };

  const submitButtonStyle = {
    flex: 2,
    padding: '12px',
    fontSize: '14px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#000',
    color: 'white',
    cursor: 'pointer',
  };

  if (!isChatOpen) {
    return (
      <button
        ref={buttonRef}
        style={chatButtonStyle}
        onClick={openChat}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#333';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#000';
        }}
      >
        💬 Ask AI
      </button>
    );
  }

  return (
    <>
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
        .chat-dot:nth-child(1) { animation-delay: -0.32s; }
        .chat-dot:nth-child(2) { animation-delay: -0.16s; }
      `}</style>
      
      <div ref={chatRef} style={chatContainerStyle}>
        {/* Chat Header - Draggable */}
        <div
          className="chat-header-drag-area"
          style={chatHeaderStyle}
          onMouseDown={handleMouseDown}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
              AI Property Assistant
            </h3>
            <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>
              Ask about your rent prediction
            </p>
          </div>
          <button
            style={closeButtonStyle}
            onClick={closeChat}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Messages Container */}
        <div style={messagesContainerStyle}>
          {error && <div style={errorStyle}>{error}</div>}

          {/* Prior conversations on this listing — collapsed behind a toggle */}
          {previousMessages.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <button
                onClick={() => setShowPrevious(!showPrevious)}
                style={{ fontSize: 12, padding: "4px 10px", border: "1px solid #ddd", borderRadius: 12,
                         background: "#f5f5f5", color: "#555", cursor: "pointer", width: "100%" }}>
                {showPrevious ? "▾ Hide" : "↩ Show"} previous conversations ({previousMessages.length} message{previousMessages.length !== 1 ? "s" : ""})
              </button>
              {showPrevious && (
                <div style={{ marginTop: 6, paddingLeft: 8, borderLeft: "2px solid #e0e0e0", opacity: 0.85 }}>
                  {previousMessages.map((m) => {
                    const isUser = m.type === "user_question" || m.type === "user_message";
                    return (
                      <div key={`prev-${m.id}`} style={{ fontSize: 12, lineHeight: 1.4, margin: "5px 0", color: "#444" }}>
                        <b style={{ color: isUser ? "#1976d2" : "#7b1fa2" }}>
                          {isUser ? (m.user || "User") : "AI"}:
                        </b>{" "}
                        <span style={{ whiteSpace: "pre-wrap" }}>{m.content}</span>
                        {m.timestamp && (
                          <span style={{ color: "#aaa", fontSize: 10 }}>
                            {"  ·  " + new Date(m.timestamp).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ textAlign: "center", fontSize: 11, color: "#999", margin: "8px 0 2px" }}>— current conversation —</div>
                </div>
              )}
            </div>
          )}

          {messages.map((message) => (
            <div key={message.id}>
              <div style={messageStyle(message.type === 'user_question')}>
                {message.content}
              </div>

              {/* Show quick action buttons for welcome message */}
              {message.showQuickActions && showQuickActions && (
                <div style={quickActionsContainerStyle}>
                  <button
                    style={quickActionButtonStyle}
                    onClick={() => handleQuickAction('exact_rent')}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#000';
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.color = '#000';
                    }}
                  >
                    💵 Exact rent change for this unit
                  </button>

                  <button
                    style={quickActionButtonStyle}
                    onClick={() => handleQuickAction('percentage')}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#000';
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.color = '#000';
                    }}
                  >
                    📊 Percentage adjustment to your prediction
                  </button>

                  <button
                    style={quickActionButtonStyle}
                    onClick={() => handleQuickAction('unit_info')}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#000';
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.color = '#000';
                    }}
                  >
                    🏠 Info about this unit
                  </button>

                  <button
                    style={quickActionButtonStyle}
                    onClick={() => handleQuickAction('building_info')}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#000';
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.color = '#000';
                    }}
                  >
                    🏢 Info about the building
                  </button>

                  {/* TODO: Implement general market insights backend
                  <button
                    style={quickActionButtonStyle}
                    onClick={() => handleQuickAction('market_insight')}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#000';
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.color = '#000';
                    }}
                  >
                    🌍 General market insights
                  </button>
                  */}

                  <div style={hintBoxStyle}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>💡 Or just type a question — Ask AI can:</div>
                    <div>• Explain a change — <i>"Why did the estimate drop from $8,200 to $7,200?"</i></div>
                    <div>• Dig into a comp — <i>"Tell me more about the 177 9th Ave comp"</i></div>
                    <div>• Building facts — <i>"What are the taxes and co-op fees?"</i></div>
                    <div>• Unit history — <i>"What did this unit rent for before?"</i></div>
                    <div>• Explore comps — <i>"Show comps if we relax the similarity filter"</i></div>
                    <div>• What-if — <i>"How much would a renovation add?"</i></div>
                    <div>• Take action — <i>"Re-run the comps" · "Exclude that comp"</i></div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Structured input form for exact rent or percentage */}
          {inputMode && (
            <div style={structuredInputContainerStyle}>
              <div style={structuredInputLabelStyle}>
                {inputMode === 'exact_rent'
                  ? '💵 Please enter the correct monthly rent for this unit:'
                  : '📊 Please enter the percentage adjustment:'}
              </div>
              <div style={structuredInputRowStyle}>
                {inputMode === 'exact_rent' && (
                  <span style={inputPrefixStyle}>$</span>
                )}
                <input
                  type="text"
                  style={structuredInputFieldStyle}
                  value={structuredInput}
                  onChange={(e) => setStructuredInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && submitStructuredFeedback()}
                  placeholder={inputMode === 'exact_rent' ? '7,500' : '-5 or +10'}
                  autoFocus
                />
                {inputMode === 'percentage' && (
                  <span style={inputSuffixStyle}>%</span>
                )}
              </div>
              <div style={structuredInputHintStyle}>
                {inputMode === 'exact_rent'
                  ? 'Enter the monthly rent amount you believe is correct for this specific unit.'
                  : 'Enter a positive number to increase (e.g., 5) or negative to decrease (e.g., -10).'}
              </div>
              <div style={structuredButtonRowStyle}>
                <button
                  style={cancelButtonStyle}
                  onClick={cancelStructuredInput}
                >
                  Cancel
                </button>
                <button
                  style={submitButtonStyle}
                  onClick={submitStructuredFeedback}
                  disabled={!structuredInput.trim() || isLoading}
                >
                  {isLoading ? 'Submitting...' : 'Submit Feedback'}
                </button>
              </div>
            </div>
          )}

          {isLoading && !inputMode && (
            <div style={messageStyle(false)}>
              <div style={loadingDotsStyle}>
                <div style={dotStyle} className="chat-dot"></div>
                <div style={dotStyle} className="chat-dot"></div>
                <div style={dotStyle} className="chat-dot"></div>
              </div>
            </div>
          )}

          {/* Show Back to Options button when quick actions are hidden and chat has started */}
          {!showQuickActions && !inputMode && sessionId && (
            <button
              style={backButtonStyle}
              onClick={() => setShowQuickActions(true)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#e0e0e0';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#f0f0f0';
              }}
            >
              ← Back to Options
            </button>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Container */}
        <div style={inputContainerStyle}>
          <textarea
            style={inputStyle}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about this rent prediction..."
            disabled={isLoading || !sessionId}
            rows={2}
          />
          <button
            style={sendButtonStyle}
            onClick={sendMessage}
            disabled={isLoading || !sessionId || !inputMessage.trim()}
            onMouseEnter={(e) => {
              if (!isLoading) e.target.style.backgroundColor = '#333';
            }}
            onMouseLeave={(e) => {
              if (!isLoading) e.target.style.backgroundColor = '#000';
            }}
          >
            {isLoading ? '...' : 'Send'}
          </button>
        </div>
      </div>
    </>
  );
};

export default ChatComponent;